/**
 * Utility per crittografia dati sensibili in IndexedDB (Web Crypto API, AES-GCM).
 */

const ENCRYPTION_KEY_NAME = 'adams_offline_key'

/**
 * `crypto.subtle` esiste solo in contesti sicuri (HTTPS, localhost).
 * Su `http://192.168.x.x:port` (dev su LAN) non è disponibile → la cifratura fallisce se non gestito.
 * @returns {boolean}
 */
export function isWebCryptoSubtleAvailable() {
  return typeof window !== 'undefined' && window.crypto != null && window.crypto.subtle != null
}

/**
 * Genera o recupera la chiave di crittografia.
 * @returns {Promise<CryptoKey>}
 */
async function getEncryptionKey() {
  const existingKey = await retrieveKey()
  if (existingKey) {
    return existingKey
  }

  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  )

  await storeKey(key)

  return key
}

/**
 * @param {CryptoKey} key
 */
async function storeKey(key) {
  const exported = await crypto.subtle.exportKey('jwk', key)
  localStorage.setItem(ENCRYPTION_KEY_NAME, JSON.stringify(exported))
}

/**
 * @returns {Promise<CryptoKey | null>}
 */
async function retrieveKey() {
  const stored = localStorage.getItem(ENCRYPTION_KEY_NAME)
  if (!stored) return null

  try {
    const jwk = JSON.parse(stored)
    return await crypto.subtle.importKey(
      'jwk',
      jwk,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    )
  } catch {
    return null
  }
}

/**
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * @param {string} base64
 * @returns {ArrayBuffer}
 */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Cripta una stringa.
 * @param {string} plaintext
 * @returns {Promise<{ iv: string, ciphertext: string }>}
 */
export async function encryptData(plaintext) {
  const key = await getEncryptionKey()
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(plaintext)

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoded
  )

  return {
    iv: arrayBufferToBase64(iv),
    ciphertext: arrayBufferToBase64(ciphertext)
  }
}

/**
 * Decripta dati precedentemente criptati con encryptData.
 * @param {{ iv: string, ciphertext: string }} encrypted
 * @returns {Promise<string>}
 */
export async function decryptData(encrypted) {
  const key = await getEncryptionKey()
  const iv = base64ToArrayBuffer(encrypted.iv)
  const ciphertext = base64ToArrayBuffer(encrypted.ciphertext)

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  )

  return new TextDecoder().decode(decrypted)
}
